import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { recipeAPI, adminAPI } from '../../services/api';
import { Plus, Edit, Trash2, LogOut, X } from 'lucide-react';

const RecipeAdmin = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    servings: '',
    prepTime: '',
    category: 'Plats principaux',
    season: 'Toute l\'année',
    image: ''
  });

  useEffect(() => {
    // Check if admin is logged in
    if (!adminAPI.isLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    fetchRecipes();
  }, [navigate]);

  const fetchRecipes = async () => {
    try {
      const data = await recipeAPI.getAll();
      setRecipes(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des recettes');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminAPI.logout();
    toast.success('Déconnexion réussie');
    navigate('/admin/login');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRecipe) {
        await recipeAPI.update(editingRecipe.id, formData);
        toast.success('Recette modifiée avec succès');
      } else {
        await recipeAPI.create(formData);
        toast.success('Recette ajoutée avec succès');
      }
      setShowForm(false);
      setEditingRecipe(null);
      resetForm();
      fetchRecipes();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      title: recipe.title,
      description: recipe.description,
      servings: recipe.servings,
      prepTime: recipe.prepTime,
      category: recipe.category,
      season: recipe.season,
      image: recipe.image
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette recette ?')) {
      return;
    }
    try {
      await recipeAPI.delete(id);
      toast.success('Recette supprimée');
      fetchRecipes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      servings: '',
      prepTime: '',
      category: 'Plats principaux',
      season: 'Toute l\'année',
      image: ''
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRecipe(null);
    resetForm();
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
              Gestion des Recettes
            </h1>
            <div className="flex gap-3">
              <Button onClick={() => setShowForm(true)} className="btn-primary">
                <Plus size={20} className="mr-2" />
                Ajouter une recette
              </Button>
              <Button onClick={handleLogout} className="btn-secondary">
                <LogOut size={20} className="mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
                  {editingRecipe ? 'Modifier la recette' : 'Nouvelle recette'}
                </h2>
                <button onClick={handleCancel} className="p-2 hover:bg-gray-100 rounded-full">
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Titre *</label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description *</label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Portions *</label>
                    <Input
                      name="servings"
                      value={formData.servings}
                      onChange={handleInputChange}
                      placeholder="50 personnes"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Temps de préparation *</label>
                    <Input
                      name="prepTime"
                      value={formData.prepTime}
                      onChange={handleInputChange}
                      placeholder="45 min"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Catégorie *</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option>Plats principaux</option>
                      <option>Entrées</option>
                      <option>Accompagnements</option>
                      <option>Salades</option>
                      <option>Soupes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Saison *</label>
                    <Input
                      name="season"
                      value={formData.season}
                      onChange={handleInputChange}
                      placeholder="Toute l'année"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">URL de l'image *</label>
                  <Input
                    name="image"
                    type="url"
                    value={formData.image}
                    onChange={handleInputChange}
                    placeholder="https://..."
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="btn-primary flex-1">
                    {editingRecipe ? 'Modifier' : 'Ajouter'}
                  </Button>
                  <Button type="button" onClick={handleCancel} className="btn-secondary flex-1">
                    Annuler
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Recipes List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recipes.map((recipe) => (
            <div key={recipe.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <img src={recipe.image} alt={recipe.title} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="font-bold text-lg mb-2">{recipe.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{recipe.description}</p>
                <div className="flex gap-2">
                  <Button onClick={() => handleEdit(recipe)} className="flex-1 text-sm py-2">
                    <Edit size={16} className="mr-1" />
                    Modifier
                  </Button>
                  <Button
                    onClick={() => handleDelete(recipe.id)}
                    className="flex-1 text-sm py-2 bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Trash2 size={16} className="mr-1" />
                    Supprimer
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RecipeAdmin;
